import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trackEvent } from '../services/analytics';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// SEO meta tags - set on mount
function useMetaTags() {
  useEffect(() => {
    document.title = 'Check Your Google Review Score - Free Rating Checker | StellarReply';
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Check your Google review score free. See your star rating, review count and recency scored against nearby competitors, what is pulling your Google rating down, and sample AI replies. No signup required.');
    setMeta('og:title', 'Check Your Google Review Score - Free Rating Checker | StellarReply');
    setMeta('og:description', 'Check your Google rating in 30 seconds: your review score, how it compares to nearby competitors, and what is dragging it down. Completely free.');
    setMeta('og:type', 'website');
    setMeta('og:url', 'https://stellarreply.com/audit');

    // Self-canonical (covers /audit and /audit/r/:shareId) — the SPA shell ships
    // the homepage canonical, which tells Google this page is a duplicate of /
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (canonical) canonical.setAttribute('href', 'https://stellarreply.com' + window.location.pathname);

    // Inject WebApplication schema for SEO
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.id = 'audit-schema';
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Free Google Review Score Checker",
      "url": "https://stellarreply.com/audit",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "Check your Google review score free. Your star rating, review volume and recency scored against nearby competitors, plus sample AI replies. No signup required.",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "GBP"
      },
      "creator": {
        "@type": "Organization",
        "name": "StellarReply",
        "url": "https://stellarreply.com"
      }
    });
    document.head.appendChild(schema);

    return () => {
      document.title = 'StellarReply';
      if (canonical && prevCanonical) canonical.setAttribute('href', prevCanonical);
      const el = document.getElementById('audit-schema');
      if (el) el.remove();
    };
  }, []);
}

function ScoreRing({ score, size = 140 }) {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 75 ? '#16a34a' : score >= 50 ? '#f59e0b' : '#dc2626';
  const label = score >= 75 ? 'Excellent' : score >= 50 ? 'Good' : score < 30 ? 'Needs Work' : 'Fair';

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#f0ede8" strokeWidth="8" />
        <circle
          cx={size/2} cy={size/2} r={radius} fill="none"
          stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1.2s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontSize: 36, fontWeight: 800, color, lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginTop: 2 }}>{label}</span>
      </div>
    </div>
  );
}

function ScoreBar({ label, score, max, color }) {
  const pct = (score / max) * 100;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: '#6b7280', fontWeight: 500 }}>{label}</span>
        <span style={{ fontWeight: 700, color }}>{score}/{max}</span>
      </div>
      <div style={{ height: 6, background: '#f0ede8', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%', background: color, borderRadius: 3,
          transition: 'width 0.8s ease',
        }} />
      </div>
    </div>
  );
}

function Stars({ rating, size = 16 }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ fontSize: size, color: i <= Math.round(rating) ? '#f59e0b' : '#ddd' }}>&#9733;</span>
      ))}
    </span>
  );
}

function SectionCard({ icon, title, color, children }) {
  return (
    <div style={{
      background: `${color}08`, borderLeft: `3px solid ${color}`,
      borderRadius: 10, padding: '16px 20px', marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ fontSize: 14, fontWeight: 700, color }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function ShareBar({ shareUrl }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 16px', background: '#f0fdf4',
      border: '1px solid rgba(22,163,74,0.15)', borderRadius: 10,
      marginBottom: 20,
    }}>
      <span style={{ fontSize: 13, color: '#16a34a', fontWeight: 600, flexShrink: 0 }}>Share this report:</span>
      <input
        type="text"
        value={shareUrl}
        readOnly
        style={{
          flex: 1, padding: '6px 10px', borderRadius: 6,
          border: '1px solid #d4d1cc', fontSize: 12,
          fontFamily: 'monospace', color: '#374151', background: '#fff',
          outline: 'none',
        }}
        onClick={e => e.target.select()}
      />
      <button
        onClick={handleCopy}
        style={{
          padding: '6px 14px', borderRadius: 6, border: 'none',
          background: copied ? '#16a34a' : '#374151', color: '#fff',
          fontSize: 12, fontWeight: 600, cursor: 'pointer',
          fontFamily: "'DM Sans', system-ui, sans-serif",
          transition: 'background 0.2s',
        }}
      >
        {copied ? 'Copied!' : 'Copy Link'}
      </button>
    </div>
  );
}

export default function AuditPage() {
  const navigate = useNavigate();
  const { shareId } = useParams();
  const [input, setInput] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [honeypot, setHoneypot] = useState('');
  const [loadedAt] = useState(() => Date.now());

  useMetaTags();

  // Load shared result if share ID is in the URL
  useEffect(() => {
    if (shareId) {
      setLoading(true);
      fetch(`${API_URL}/audit/r/${shareId}`)
        .then(res => {
          if (!res.ok) throw new Error('Result not found');
          return res.json();
        })
        .then(data => setResult(data))
        .catch(() => setError('This analysis link was not found. It may have expired.'))
        .finally(() => setLoading(false));
    }
  }, [shareId]);

  const isUrl = input.includes('google.com/maps') || input.includes('maps.google') || input.includes('g.page');

  const handleAudit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const body = isUrl
        ? { google_maps_url: input.trim(), website: honeypot, loaded_at: loadedAt }
        : { business_name: input.trim(), location: location.trim() || null, website: honeypot, loaded_at: loadedAt };

      const res = await fetch(`${API_URL}/audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        trackEvent('audit_failed', { status: res.status });
        throw new Error(data.detail || 'Analysis failed. Please check the URL or business name.');
      }

      const data = await res.json();
      setResult(data);
      trackEvent('audit_completed', { score: data.reputation_score });

      // Update browser URL to shareable link without reloading
      if (data.share_id) {
        window.history.replaceState(null, '', `/audit/r/${data.share_id}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <style>{cssText}</style>

      {/* Nav */}
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          <div style={styles.logo} onClick={() => navigate('/')}>
            Stellar<span style={{ color: '#16a34a' }}>Reply</span>
          </div>
          <button style={styles.navCta} onClick={() => navigate('/login?register=true')}>
            Start free trial
          </button>
        </div>
      </nav>

      {/* Hero / Form */}
      {!result && !loading && !shareId && (
        <section style={styles.hero}>
          <div style={styles.heroInner}>
            <div style={styles.badge}>Free tool - no signup required</div>
            <h1 style={styles.h1}>
              Check your<br />
              <span style={{ color: '#16a34a' }}>Google review score</span>
            </h1>
            <p style={styles.heroSub}>
              Type your business name to check your Google rating free. You will see your
              review score out of 100, how your star rating compares to nearby competitors,
              what is pulling it down, and sample AI replies to your latest reviews.
            </p>

            <form onSubmit={handleAudit} style={styles.form}>
              <div style={styles.inputGroup}>
                <input
                  style={styles.input}
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Type your business name..."
                  disabled={loading}
                />
                {!isUrl && input.length > 0 && (
                  <input
                    style={{ ...styles.input, marginTop: 10 }}
                    type="text"
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                    placeholder="Location (e.g. Bromley, London)"
                    disabled={loading}
                  />
                )}
              </div>
              {/* Honeypot */}
              <input
                type="text" value={honeypot} onChange={e => setHoneypot(e.target.value)}
                autoComplete="off" tabIndex={-1}
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                aria-hidden="true"
              />
              <button
                style={{ ...styles.btn, opacity: loading || !input.trim() ? 0.7 : 1 }}
                type="submit" disabled={loading || !input.trim()}
              >
                {loading ? 'Analysing...' : 'Analyse My Reviews'}
              </button>
            </form>

            {error && (
              <div style={styles.errorBox} className="audit-fade">{error}</div>
            )}

            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 20, textAlign: 'center' }}>
              Works with any business on Google Maps. 100% free, no signup needed.
            </p>
          </div>
        </section>
      )}

      {/* Loading state */}
      {loading && (
        <section style={{ padding: '80px 20px', textAlign: 'center' }}>
          <div style={{ maxWidth: 400, margin: '0 auto' }}>
            <div style={styles.spinner} className="audit-spin" />
            <div style={{ fontWeight: 600, color: '#1a1a1a', fontSize: 16, marginTop: 16 }}>
              {shareId ? 'Loading report...' : 'Analysing your reputation...'}
            </div>
            {!shareId && (
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 8, lineHeight: 1.5 }}>
                Fetching reviews, finding competitors, and generating your report. This takes about 15 seconds.
              </div>
            )}
          </div>
        </section>
      )}

      {/* Results */}
      {result && (
        <section style={styles.results} className="audit-fade">
          <div style={styles.resultsInner}>
            {/* Header */}
            <div style={styles.resultsHeader}>
              <div>
                <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 4px', color: '#1a1a1a' }}>
                  {result.business_name}
                </h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {result.rating && <Stars rating={result.rating} size={18} />}
                    <span style={{ fontSize: 14, color: '#6b7280' }}>
                        {result.rating
                            ? `${result.rating}/5 from ${result.total_reviews} reviews`
                            : result.total_reviews > 0
                                ? `${result.total_reviews} reviews`
                                : 'No reviews yet'}
                    </span>
                </div>
              </div>
              <button style={styles.newAuditBtn} onClick={() => {
                setResult(null); setInput(''); setError('');
                navigate('/audit');
              }}>
                New Analysis
              </button>
            </div>

            {/* Share bar */}
            {result.share_url && <ShareBar shareUrl={result.share_url} />}

            {/* Score + Breakdown */}
            <div style={styles.scoreGrid}>
              <div style={styles.scoreCard}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 16 }}>
                  Reputation Score
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                  <ScoreRing score={result.reputation_score} />
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap',
                }}>
                  {[
                    { label: 'Needs Work', min: 0, max: 29, color: '#dc2626' },
                    { label: 'Fair', min: 30, max: 49, color: '#f59e0b' },
                    { label: 'Good', min: 50, max: 74, color: '#f59e0b' },
                    { label: 'Excellent', min: 75, max: 100, color: '#16a34a' },
                  ].map(t => {
                    const active = result.reputation_score >= t.min && result.reputation_score <= t.max;
                    return (
                      <span key={t.label} style={{
                        fontSize: 10, fontWeight: active ? 700 : 500,
                        padding: '3px 8px', borderRadius: 4,
                        background: active ? `${t.color}12` : 'transparent',
                        color: active ? t.color : '#9ca3af',
                        border: active ? `1px solid ${t.color}30` : '1px solid transparent',
                      }}>
                        {t.label} ({t.min}-{t.max})
                      </span>
                    );
                  })}
                </div>
              </div>
              <div style={styles.scoreCard}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 16 }}>
                  Score Breakdown
                </div>
                <ScoreBar label="Rating Quality" score={result.score_breakdown.rating} max={35} color="#16a34a" />
                <ScoreBar label="Review Volume" score={result.score_breakdown.volume} max={25} color="#2563eb" />
                <ScoreBar label="Review Recency" score={result.score_breakdown.recency} max={20} color="#9333ea" />
                <ScoreBar label="vs Competitors" score={result.score_breakdown.competitive} max={20} color="#d97706" />
              </div>
            </div>

            {/* Analysis */}
            {result.analysis && (
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>AI Analysis</h3>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: '#374151', margin: 0, whiteSpace: 'pre-wrap' }}>
                  {result.analysis}
                </p>
              </div>
            )}

            {/* Strengths + Weaknesses + Quick Wins */}
            <div style={styles.insightsGrid}>
              {result.strengths.length > 0 && (
                <SectionCard icon="&#128170;" title="Strengths" color="#16a34a">
                  {result.strengths.map((s, i) => (
                    <div key={i} style={styles.insightItem}>
                      <span style={{ color: '#16a34a', flexShrink: 0 }}>&#10003;</span>
                      <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{s}</span>
                    </div>
                  ))}
                </SectionCard>
              )}
              {result.weaknesses.length > 0 && (
                <SectionCard icon="&#9888;" title="Areas to Improve" color="#dc2626">
                  {result.weaknesses.map((w, i) => (
                    <div key={i} style={styles.insightItem}>
                      <span style={{ color: '#dc2626', flexShrink: 0 }}>&#8226;</span>
                      <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{w}</span>
                    </div>
                  ))}
                </SectionCard>
              )}
              {result.quick_wins.length > 0 && (
                <SectionCard icon="&#127942;" title="Quick Wins" color="#2563eb">
                  {result.quick_wins.map((q, i) => (
                    <div key={i} style={styles.insightItem}>
                      <span style={{ color: '#2563eb', flexShrink: 0 }}>{i + 1}.</span>
                      <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{q}</span>
                    </div>
                  ))}
                </SectionCard>
              )}
            </div>

            {/* Competitors */}
            {result.competitors.length > 0 && (
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Nearby Competitors</h3>
                {result.competitors.map((c, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 0',
                    borderBottom: i < result.competitors.length - 1 ? '1px solid #f0ede8' : 'none',
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a' }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{c.total_reviews} reviews</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Stars rating={c.rating || 0} size={12} />
                      <span style={{
                        fontWeight: 800, fontSize: 15,
                        color: (c.rating || 0) > (result.rating || 0) ? '#dc2626' : '#16a34a',
                      }}>{c.rating ? c.rating.toFixed(1) : '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Sample AI Responses */}
            {result.reviews.some(r => r.sample_response) && (
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Sample AI Responses</h3>
                <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 16px' }}>
                  Here's how StellarReply would respond to your latest reviews.
                </p>
                {result.reviews.filter(r => r.sample_response).map((r, i) => (
                  <div key={i} style={{
                    marginBottom: 16, padding: '16px', borderRadius: 10,
                    border: '1px solid #e8e5df', background: '#faf9f6',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: ['#16a34a','#2563eb','#9333ea'][i % 3],
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, color: '#fff',
                      }}>{(r.reviewer_name || '?')[0].toUpperCase()}</div>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{r.reviewer_name}</span>
                        <Stars rating={r.rating} size={11} />
                      </div>
                    </div>
                    <p style={{ fontSize: 12.5, color: '#6b7280', margin: '0 0 10px', lineHeight: 1.5 }}>
                      "{r.text}"
                    </p>
                    <div style={{
                      padding: '12px 14px', borderRadius: 8,
                      background: 'rgba(22,163,74,0.06)', borderLeft: '3px solid #16a34a',
                    }}>
                      <div style={{ fontSize: 10, color: '#16a34a', fontWeight: 700, marginBottom: 4 }}>
                        AI Response by StellarReply
                      </div>
                      <p style={{ fontSize: 13, color: '#1a1a1a', margin: 0, lineHeight: 1.6 }}>
                        {r.sample_response}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Next step: the exact arithmetic behind the score above */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Work out your exact target</h3>
              <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.6 }}>
                Now you know where {result.business_name} stands, the{' '}
                <a href="/google-rating-calculator" style={{ color: '#16a34a', fontWeight: 600 }}>
                  Google rating calculator
                </a>{' '}
                works out exactly how many 5-star reviews it takes to reach 4.5, and how many bad
                reviews you can absorb before you slip below it.
              </p>
            </div>

            {/* CTA */}
            <div style={styles.ctaCard}>
              <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px', color: '#1a1a1a' }}>
                Put {result.business_name}'s reviews on autopilot
              </h2>
              <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 20px', lineHeight: 1.6 }}>
                StellarReply writes personalised, on-brand responses to every review {result.business_name} gets,
                protects you from negative public feedback, and helps you outperform the competitors above.
                Starting at just &#163;20/month.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button style={styles.btn} onClick={() => navigate('/login?register=true')}>
                  Start 14-day free trial
                </button>
                <button style={styles.btnGhost} onClick={() => navigate('/')}>
                  Learn more
                </button>
              </div>
              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 12 }}>
                No credit card required. Cancel anytime.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer style={styles.footer}>
        <span>Powered by </span>
        <a href="https://stellarreply.com" style={{ color: '#16a34a', textDecoration: 'none', fontWeight: 600 }}>
          StellarReply
        </a>
      </footer>
    </div>
  );
}

const cssText = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap');
  @keyframes auditFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes auditSpin { to { transform: rotate(360deg); } }
  .audit-fade { animation: auditFadeIn 0.5s ease both; }
  .audit-spin { animation: auditSpin 0.8s linear infinite; }
`;

const styles = {
  page: { fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif", minHeight: '100vh', background: '#faf9f6' },
  nav: { borderBottom: '1px solid #e8e5df', background: '#fff' },
  navInner: { maxWidth: 900, margin: '0 auto', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { fontSize: 18, fontWeight: 800, color: '#1a1a1a', cursor: 'pointer' },
  navCta: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  hero: { padding: '60px 20px 40px', textAlign: 'center' },
  heroInner: { maxWidth: 600, margin: '0 auto' },
  badge: { display: 'inline-block', fontSize: 12, fontWeight: 600, color: '#16a34a', background: 'rgba(22,163,74,0.08)', padding: '4px 14px', borderRadius: 20, marginBottom: 16 },
  h1: { fontSize: 36, fontWeight: 800, color: '#1a1a1a', lineHeight: 1.2, margin: '0 0 16px' },
  heroSub: { fontSize: 15, color: '#6b7280', lineHeight: 1.6, margin: '0 0 32px', maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' },
  form: { maxWidth: 500, margin: '0 auto' },
  inputGroup: { marginBottom: 12 },
  input: { width: '100%', padding: '14px 18px', borderRadius: 10, border: '1px solid #d4d1cc', fontSize: 15, fontFamily: "'DM Sans', system-ui, sans-serif", outline: 'none', boxSizing: 'border-box', background: '#fff' },
  btn: { width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #16a34a, #059669)', color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  btnGhost: { padding: '12px 24px', borderRadius: 10, border: '1px solid #d4d1cc', background: '#fff', color: '#1a1a1a', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  spinner: { width: 32, height: 32, border: '3px solid #e0ddd8', borderTopColor: '#16a34a', borderRadius: '50%', margin: '0 auto' },
  errorBox: { marginTop: 16, padding: '12px 16px', background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.15)', borderRadius: 8, color: '#dc2626', fontSize: 13 },
  results: { padding: '24px 20px 40px' },
  resultsInner: { maxWidth: 700, margin: '0 auto' },
  resultsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, padding: '20px 24px', background: '#fff', borderRadius: 12, border: '1px solid #e8e5df' },
  newAuditBtn: { background: 'none', border: '1px solid #d4d1cc', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#6b7280', fontFamily: "'DM Sans', system-ui, sans-serif" },
  scoreGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 },
  scoreCard: { background: '#fff', borderRadius: 12, border: '1px solid #e8e5df', padding: '20px 24px' },
  card: { background: '#fff', borderRadius: 12, border: '1px solid #e8e5df', padding: '20px 24px', marginBottom: 16 },
  cardTitle: { fontSize: 15, fontWeight: 700, margin: '0 0 12px', color: '#1a1a1a' },
  insightsGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: 0, marginBottom: 16 },
  insightItem: { display: 'flex', gap: 8, padding: '4px 0' },
  ctaCard: { background: '#fff', borderRadius: 12, border: '2px solid #16a34a', padding: '32px', textAlign: 'center', marginTop: 8 },
  footer: { textAlign: 'center', padding: '20px', fontSize: 12, color: '#9ca3af', borderTop: '1px solid #e8e5df' },
};
